import '../../Styles/App.css';
import '../../Styles/Admin.css';
import { useCallback, useEffect, useState } from 'react';
import { MdStar, MdStarBorder, MdArrowUpward, MdArrowDownward, MdAlbum, MdSearch } from 'react-icons/md';
import izenuAPI from '../../services/izenuAPI';

function Cover({ url, size = 64 }) {
    const [err, setErr] = useState(false);
    if (!url || err) {
        return (
            <div style={{
                width: size, height: size, borderRadius: 8, flexShrink: 0,
                backgroundColor: '#eee', display: 'flex',
                alignItems: 'center', justifyContent: 'center',
            }}>
                <MdAlbum size={size * 0.45} color="#999" />
            </div>
        );
    }
    return <img src={url} alt="" onError={() => setErr(true)}
        style={{ width: size, height: size, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }} />;
}

function price(r) {
    if (r.is_name_your_price) return 'Paga o que quiseres';
    if (r.price_aoa) return `${new Intl.NumberFormat('pt-AO').format(r.price_aoa)} Kz`;
    if (r.price_usd) return `$${Number(r.price_usd).toFixed(2)}`;
    return 'Grátis';
}

export default function StoreFeatured() {
    const [items, setItems] = useState([]);
    const [counts, setCounts] = useState({ total: 0, featured: 0 });
    const [only, setOnly] = useState('all');
    const [q, setQ] = useState('');
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data: r } = await izenuAPI.get('/admin/releases/listForSale.php', {
                params: { only, q, limit: 200 },
            });
            if (r.response) {
                setItems(r.data);
                setCounts(r.counts);
            }
        } catch (err) {
            alert('Erro ao carregar: ' + (err?.response?.data?.message || err.message));
        }
        setLoading(false);
    }, [only, q]);

    useEffect(() => { load(); }, [only]); // eslint-disable-line react-hooks/exhaustive-deps

    const toggle = useCallback(async (rel) => {
        if (busy) return;
        const next = rel.featured ? 0 : 1;
        setBusy(rel.id);
        setItems(prev => prev.map(x => x.id === rel.id ? { ...x, featured: next } : x));
        try {
            const { data: r } = await izenuAPI.post('/admin/releases/toggleFeatured.php', {
                release_id: rel.id, featured: next,
            });
            if (!r.response) throw new Error(r.message);
            await load();
        } catch (err) {
            setItems(prev => prev.map(x => x.id === rel.id ? { ...x, featured: rel.featured } : x));
            alert('Erro: ' + (err?.response?.data?.message || err.message));
        }
        setBusy(null);
    }, [busy, load]);

    // Ordering applies to the featured subset only, so the handler derives its
    // own list from the master array and writes back by id.
    const move = useCallback(async (id, dir) => {
        const list = items
            .filter(x => x.featured)
            .sort((a, b) => (a.featured_order ?? 9999) - (b.featured_order ?? 9999));
        const idx = list.findIndex(x => x.id === id);
        const next = idx + dir;
        if (idx < 0 || next < 0 || next >= list.length) return;
        [list[idx], list[next]] = [list[next], list[idx]];

        const orderMap = new Map(list.map((x, i) => [x.id, i + 1]));
        const snapshot = items;
        setItems(prev => prev.map(x => orderMap.has(x.id) ? { ...x, featured_order: orderMap.get(x.id) } : x));

        try {
            const { data: r } = await izenuAPI.post('/admin/releases/reorderFeatured.php', {
                release_ids: list.map(x => x.id),
            });
            if (!r.response) throw new Error(r.message);
        } catch (err) {
            setItems(snapshot);
            alert('Erro ao reordenar: ' + (err?.response?.data?.message || err.message));
        }
    }, [items]);

    const featuredList = items
        .filter(x => x.featured)
        .sort((a, b) => (a.featured_order ?? 9999) - (b.featured_order ?? 9999));

    const visible = only === 'featured' ? featuredList : items;

    const chip = (active) => ({
        padding: '8px 18px', borderRadius: 20, border: '1px solid #e0e0e0', cursor: 'pointer',
        fontSize: 14, fontWeight: 600,
        backgroundColor: active ? '#1976d2' : '#fff',
        color: active ? '#fff' : '#666',
    });
    const iconBtn = (disabled) => ({
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        width: 30, height: 30, borderRadius: 6,
        border: '1px solid #e0e0e0', backgroundColor: '#fff',
        cursor: disabled ? 'default' : 'pointer',
        color: disabled ? '#ccc' : '#555',
    });

    return (
        <div style={{ padding: 24 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 8, flexWrap: 'wrap' }}>
                <h2 style={{ margin: 0, fontSize: 22, color: '#222' }}>Álbuns em Destaque</h2>
                <span style={{ fontSize: 13, color: '#888' }}>
                    {counts.featured} em destaque de {counts.total} à venda
                </span>
            </div>
            <p style={{ margin: '4px 0 20px', maxWidth: 720, fontSize: 14, lineHeight: 1.5, color: '#555', textAlign: 'left' }}>
                Escolhe os álbuns à venda que aparecem na secção «À Venda na Loja», na home da app e do site. Marca com a
                estrela os que queres mostrar e ordena-os com as setas: a ordem aqui é a ordem na home. Sem nenhum em
                destaque, a secção não aparece. Esta escolha é só da Izenu — os distribuidores não podem destacar os
                próprios álbuns.
            </p>

            <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
                <button onClick={() => setOnly('all')} style={chip(only === 'all')}>Todos à venda</button>
                <button onClick={() => setOnly('featured')} style={chip(only === 'featured')}>Só em destaque</button>
                <div style={{ display: 'flex', alignItems: 'center', backgroundColor: '#fff', borderRadius: 8, padding: '6px 12px', border: '1px solid #ccc' }}>
                    <MdSearch size={18} color="#888" />
                    <input
                        type="text"
                        value={q}
                        placeholder="Pesquisar…"
                        onChange={(e) => setQ(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') load(); }}
                        onBlur={load}
                        style={{ border: 'none', outline: 'none', marginLeft: 6, fontSize: 14, width: 180 }}
                    />
                </div>
            </div>

            {loading && <p style={{ color: '#888' }}>A carregar…</p>}
            {!loading && visible.length === 0 && (
                <p style={{ color: '#888' }}>
                    {only === 'featured'
                        ? 'Nenhum álbum em destaque — a secção não aparece na home.'
                        : 'Nenhum álbum à venda.'}
                </p>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {visible.map((r) => {
                    const fIdx = featuredList.findIndex(x => x.id === r.id);
                    return (
                        <div key={r.id} style={{
                            backgroundColor: '#fff', borderRadius: 12, padding: 14,
                            border: `1px solid ${r.featured ? '#f5a623' : '#e0e0e0'}`,
                            display: 'flex', gap: 14, alignItems: 'center',
                        }}>
                            {r.featured && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                    <button onClick={() => move(r.id, -1)} disabled={fIdx === 0} style={iconBtn(fIdx === 0)}>
                                        <MdArrowUpward size={16} />
                                    </button>
                                    <button onClick={() => move(r.id, 1)} disabled={fIdx === featuredList.length - 1} style={iconBtn(fIdx === featuredList.length - 1)}>
                                        <MdArrowDownward size={16} />
                                    </button>
                                </div>
                            )}
                            {r.featured && (
                                <span style={{ fontSize: 13, color: '#999', width: 18, textAlign: 'center' }}>{fIdx + 1}</span>
                            )}

                            <Cover url={r.artwork} />

                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 15, fontWeight: 600, color: '#222' }}>{r.title}</div>
                                <div style={{ fontSize: 13, color: '#666', marginTop: 2 }}>
                                    {r.artist_name} · <span style={{ textTransform: 'capitalize' }}>{r.type}</span> · {r.track_count} faixas · {price(r)}
                                </div>
                                {r.distributor_name && (
                                    <div style={{ fontSize: 12, color: '#999', marginTop: 2 }}>
                                        Distribuidor: {r.distributor_name}
                                    </div>
                                )}
                            </div>

                            <button
                                onClick={() => toggle(r)}
                                disabled={busy === r.id}
                                title={r.featured ? 'Remover da home' : 'Destacar na home'}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: 6,
                                    padding: '8px 16px', borderRadius: 8, cursor: 'pointer',
                                    border: `1px solid ${r.featured ? '#f5a623' : '#e0e0e0'}`,
                                    backgroundColor: r.featured ? '#fff8e1' : '#fff',
                                    color: r.featured ? '#b26a00' : '#666',
                                    fontSize: 13, fontWeight: 600, flexShrink: 0,
                                    opacity: busy === r.id ? 0.5 : 1,
                                }}
                            >
                                {r.featured ? <MdStar size={18} /> : <MdStarBorder size={18} />}
                                {r.featured ? 'Destacado' : 'Destacar'}
                            </button>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
